import { useEffect, useState, useRef } from "react";
import { Megaphone, Upload, Trash2, Plus, Loader2, ExternalLink, Eye, EyeOff, RefreshCw } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState({ image: "", link: "", title: "", starts_at: "", ends_at: "", coupon_code: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/banners").then((r) => setBanners(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/files/upload?visibility=public", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image: `${BACKEND}${data.url}` }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const add = async () => {
    if (!form.image) return toast.error("Pehle banner image upload ya URL daalein");
    setSaving(true);
    try {
      await api.post("/admin/banners", { ...form, active: true });
      toast.success("Banner added — app par bhi sync hoga");
      setForm({ image: "", link: "", title: "", starts_at: "", ends_at: "", coupon_code: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const toggle = async (b) => {
    try { await api.put(`/admin/banners/${b.id}`, { active: !b.active }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (b) => {
    if (!window.confirm("Ye banner delete karein? (app se bhi hat jayega)")) return;
    try { await api.delete(`/admin/banners/${b.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const _today = new Date().toISOString().slice(0, 10);
  const schedLabel = (b) => {
    if (!b.active) return null;
    if (b.starts_at && _today < String(b.starts_at).slice(0, 10)) return { t: "SCHEDULED", c: "bg-amber-100 text-amber-700" };
    if (b.ends_at && _today > String(b.ends_at).slice(0, 10)) return { t: "EXPIRED", c: "bg-slate-200 text-slate-500" };
    return null;
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="admin-banners-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center"><Megaphone size={22} /></div>
          <div>
            <h1 className="text-xl md:text-2xl font-extrabold text-slate-900">Homepage Banners</h1>
            <p className="text-sm text-slate-500">Website hero banners — app ke saath 2-way sync. App se change hoga to yahan bhi dikhega.</p>
          </div>
        </div>
        <button onClick={load} data-testid="banners-refresh" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50"><RefreshCw size={15} /> Refresh</button>
      </div>

      {/* Add new banner */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5" data-testid="banner-add-form">
        <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2"><Plus size={17} /> Add Banner</h2>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="banner-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="banner-upload-btn"
              className="w-full h-28 rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-400 flex flex-col items-center justify-center text-slate-500 gap-1 overflow-hidden">
              {uploading ? <Loader2 className="animate-spin" size={22} /> : form.image ? <img src={form.image} alt="preview" className="w-full h-full object-cover" /> : <><Upload size={22} /><span className="text-xs font-semibold">Upload image</span></>}
            </button>
          </div>
          <div className="md:col-span-2 space-y-2">
            <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Image URL (ya upload karein)" data-testid="banner-image-url" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (optional)" data-testid="banner-title" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="Link URL (optional, https://…)" data-testid="banner-link" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-slate-500">Start date (optional)<input type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="banner-starts-at" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" /></label>
              <label className="text-xs text-slate-500">End date (optional)<input type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="banner-ends-at" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" /></label>
            </div>
            <input value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase() })} placeholder="Coupon code (optional — banner tap par cart me auto-apply)" data-testid="banner-coupon" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <button onClick={add} disabled={saving} data-testid="banner-save-btn" className="inline-flex items-center gap-2 bg-emerald-600 text-white font-bold px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add Banner</button>
          </div>
        </div>
      </div>

      {/* Existing banners */}
      {banners.length === 0 ? (
        <div className="text-center py-14 text-slate-400" data-testid="banners-empty">Koi banner nahi. Upar se add karein.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="banners-grid">
          {banners.map((b) => (
            <div key={b.id} data-testid={`banner-card-${b.id}`} className={`bg-white border rounded-2xl overflow-hidden ${b.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
              <div className="aspect-[16/7] bg-slate-100">
                {b.image ? <img src={b.image} alt={b.title || "banner"} className="w-full h-full object-cover" /> : null}
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${b.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{b.active ? "LIVE" : "HIDDEN"}</span>
                  {(() => { const s = schedLabel(b); return s ? <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.c}`} data-testid={`banner-sched-${b.id}`}>{s.t}</span> : null; })()}
                  {b._sync_origin && <span className="text-[10px] text-slate-400">from {b._sync_origin === "owner" ? "app" : "web"}</span>}
                  {b.coupon_code && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700" data-testid={`banner-coupon-${b.id}`}>Coupon: {b.coupon_code}</span>}
                </div>
                {(b.starts_at || b.ends_at) && <div className="text-[11px] text-slate-400">{b.starts_at ? `From ${String(b.starts_at).slice(0, 10)}` : "Any"} → {b.ends_at ? String(b.ends_at).slice(0, 10) : "no end"}</div>}
                <div className="text-sm font-semibold text-slate-800 truncate">{b.title || <span className="text-slate-400">(no title)</span>}</div>
                {b.link && <a href={b.link} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 truncate"><ExternalLink size={12} /> {b.link}</a>}
                <div className="flex items-center gap-1 pt-1">
                  <button onClick={() => toggle(b)} data-testid={`banner-toggle-${b.id}`} className="flex-1 text-xs font-semibold px-2 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center justify-center gap-1">{b.active ? <><EyeOff size={13} /> Hide</> : <><Eye size={13} /> Show</>}</button>
                  <button onClick={() => del(b)} data-testid={`banner-delete-${b.id}`} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
